import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Logo from "@/components/Logo";
import PhoneOtpAuth from "@/components/PhoneOtpAuth";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome back!");
      nav(r.user.role === "admin" ? "/admin/dashboard" : "/dashboard");
    } else {
      toast.error(r.error);
    }
  };

  const fillDemo = () => { setEmail("demo@yonsii.com"); setPassword("Demo@1234"); };

  return (
    <div className="min-h-screen">
      <Navbar variant="public" />
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="soft-card p-8">
          <Logo variant="auth" testId="login-logo" className="mb-6" />
          <div className="chip-light mb-3 mx-auto w-fit">User sign in</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 text-center">Welcome back</h1>
          <p className="text-slate-500 mt-1 text-center">Access your dashboard and case logs.</p>

          <Tabs defaultValue="phone" className="mt-6" data-testid="login-auth-tabs">
            <TabsList className="grid grid-cols-2 bg-slate-100 border border-slate-200 p-1 rounded-xl w-full">
              <TabsTrigger value="phone" data-testid="login-tab-phone" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
                Phone + OTP
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="login-tab-email" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold">
                Email
              </TabsTrigger>
            </TabsList>

            <TabsContent value="phone" className="mt-5">
              <PhoneOtpAuth mode="login" redirectTo="/dashboard" />
            </TabsContent>

            <TabsContent value="email" className="mt-5">
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="font-semibold">Email</Label>
                  <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 border border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500 h-11" />
                </div>
                <div>
                  <Label htmlFor="password" className="font-semibold">Password</Label>
                  <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 border border-slate-200 focus-visible:ring-2 focus-visible:ring-blue-500 h-11" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full bg-brand hover:bg-brand-dark text-white rounded-full shadow-btn font-bold h-11">
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>
              <div className="mt-5 text-center">
                <button data-testid="demo-user-login-btn" onClick={fillDemo} className="text-sm font-semibold text-blue-700 underline">Use demo user credentials</button>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-sm text-slate-500 text-center">
            No account? <Link to="/register" className="font-semibold text-slate-900 underline" data-testid="link-register">Book your first session</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
